import { useNavigate, Link } from "src/navigation";
import { useTranslation } from "react-i18next"
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import _ from 'lodash'

import Modal from "src/components/Modal";
import { eliminateDoubleRenewals } from "src/renewal/utils/renewals";
import { useState } from "react";
import { IconSpinner } from "src/components/icons";
import { useComments } from "src/comments/CommentsProvider";
import { family_member } from "src/data";
import { useRenewalOverview } from "../interfaces/Renwals";
import { DueDateStatus, Renewal } from "../interfaces/renewal_types";
import { FeeAmount, StatusView } from "../components/InstructionOverview";
import { extractDueDateStatus } from "../components/dueDates";
import { DocumentInfo, useDocumentContext, useIpRightDocuments } from "../interfaces/Documents";
import { DocumentProvider, IpRightDocumentsProvider } from "../interfaces/DocumentsProvider";

export function RenewalsTrail() {
  const { ipRight, member } = useRenewalOverview()

  if (ipRight === undefined || member === undefined) return null

  // console.log({ ipRight, member })
  return (
    <IpRightDocumentsProvider {...{ ipRightId: ipRight.id }}>
      <RenewalsDocsTrail />
    </IpRightDocumentsProvider>
  )
}

function RenewalsDocsTrail() {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const {ipRight, member, renewals} = useRenewalOverview()
    const {documents} = useIpRightDocuments()

    const annuityCertificates = documents.filter(d => d.type === 'annuity-certificate')

    function logdata() {
        console.log({ipRight, renewals, annuityCertificates})
    }

    const noData = annuityCertificates.length === 0 && renewals.length === 0

    return (
      <IpRightDocumentsProvider {...{ ipRightId: ipRight.id }}>
        <Modal escAction={() => navigate('..')} blurClick={() => navigate('..')}>
          <div className="p-4 bg-pcx-100">
            <Link className="text-slate-500 capitalize text-sm mb-4" to={'..'}>
              <ChevronLeftIcon className="h-4 w-4 inline-block mb-0.5" /> {t('back')}
            </Link>
            <h3 onClick={logdata} className="mb-2">
              {t('instructions')}
              {member ? ': ' + member?.internalReference : ''}
            </h3>
            {noData ? (
              <p className="p-4 bg-white rounded-lg shadow text-center text-slate-800 text-lg">No data yet.</p>
            ) : (
              <Trail {...{ renewals, annuityCertificates }} />
            )}
          </div>
          <div className="bg-pcx-100 flex flex-row-reverse p-4 pt-0">
            <Link className="btn-secondary capitalize" to={'..'}>
              {t('close')}
            </Link>
          </div>
        </Modal>
      </IpRightDocumentsProvider>
    )
}

function Trail({ renewals, annuityCertificates }: { renewals: Renewal[], annuityCertificates: DocumentInfo[] }) {
    const renewalsByYear = eliminateDoubleRenewals(renewals)
    const certificatesByYear = _.keyBy(annuityCertificates, a => a.created?.slice(0, 4) ?? '-')
    const years = _(renewalsByYear).keys().concat(_.keys(certificatesByYear)).uniq().sort().reverse().value()
    //console.log({maintenanceActionsByYear, certificatesByYear, years})
    return (
        <div className="grid grid-cols-2 md:grid-cols-[auto_auto_auto_auto] gap-x-4 lg:gap-x-8 gap-y-2">
            {years.map( year => {
                const renewal = renewalsByYear[year]
                return renewal 
                    ? <RenewalHistory key={renewal.id} {...{ renewal: renewal, certificate: certificatesByYear[year] }} />
                    : <CertificateView key={year} {...{certificate: certificatesByYear[year]}} />
            })}
        </div>
    )
}

function CertificateView({certificate}: {certificate?: DocumentInfo}) {
    if (!certificate) return null

    return (
      <DocumentProvider id={certificate.id}>
        <div className="col-span-2 md:col-span-4 p-4 bg-white rounded-lg shadow">
          <DownloadButton documentId={certificate.id} />
        </div>
      </DocumentProvider>
    )
}

function RenewalHistory({renewal, certificate}: {renewal: Renewal, certificate?: DocumentInfo}) {
    const {t} = useTranslation()

    const {member} = useRenewalOverview()
    const familyMemberId = member?.familyMemberId
    const {instruction, instructionDueDate, fee} = renewal
    const {commentsLookUp, commentById} = useComments()

    const instructionComment = commentById[instruction?.commentId]
    // either show specific comment for instruction or all comments from the same day
    const comments = instructionComment ? [instructionComment] : instruction?.created === undefined ? [] : (commentsLookUp[family_member]?.[familyMemberId] ?? [])
        .filter(c => {
            const date = c.created.substring(0, 10)
            const iDate = instruction.created.substring(0, 10)
            return date === iDate
        })
    //console.log({instructionComment, comments, instruction})
    const today = new Date().toISOString().slice(0, 10)
    const status = extractDueDateStatus(renewal, today)
    
    return (
        <div className="col-span-2 md:col-span-4 grid grid-cols-subgrid gap-y-2 items-baseline p-4 bg-white rounded-lg shadow">
            <div className="max-md:col-span-2 grid grid-cols-[auto_auto] gap-x-2 items-baseline" title={renewal.feeDueDate}>
                <span className="md:text-lg font-semibold text-pcx-900 tabular-nums">{instructionDueDate}</span>
                <span className="text-slate-600 whitespace-nowrap text-sm">{t('instruction-due')}</span>
                <span className="text-slate-500 font-semibold md:text-lg tabular-nums">{renewal.feeDueDate}</span>
                <span className="text-slate-600 whitespace-nowrap text-sm">  {t('renewal-fee-due')}</span>
            </div>
            <div className="max-md:col-span-2">
                <StatusView {...{ renewal, status, instruction }} />
                {comments.map(({comment}, ci) => <div key={ci} className="text-slate-600 text-sm mt-2 max-w-prose">{comment}</div>)}
            </div>
            <div>
                {_(fee).toPairs().map(([currency, amount]) => <FeeAmount key={currency} {...{ currency, amount }} />).value()}
                {renewal.annuity &&
                    <div className="whitespace-nowrap text-sm text-slate-600 mt-1.5">{t('annuity')}: {renewal.annuity}</div>}
            </div>
            <div className="justify-self-end self-center"><ActionView {...{ status, renewal: renewal }} /></div>

            <div className="col-span-2 md:col-span-4 empty:hidden flex flex-col gap-1">
                {(renewal.documents?.debitNotes ?? []).map((documentId) =>
                    <DownloadButtonWithContext key={documentId} {...{documentId}} />)}
                {(renewal.documents?.renewalNotices ?? []).map((documentId) =>
                    <DownloadButtonWithContext key={documentId} {...{documentId}} />)}
                {certificate && <DownloadButtonWithContext documentId={certificate.id} />}
            </div> 
        </div>
    )
}

function DownloadButtonWithContext({ documentId }: { documentId: string }) {
  return <DocumentProvider id={documentId} >
    <DownloadButton key={documentId} {...{ documentId }} />
  </DocumentProvider>
}

/*
  Type (Date): Name
  Type: Name
  (Date): Name
  Name
*/
function DownloadButton({documentId}) {
    const {t} = useTranslation()

    const {download, info} = useDocumentContext()
    const [isDownloading, setIsDownloading] = useState(false)

    if (!info) return null

    const documentType = info.type ? t(info.type) : null
    const documentDate = info.created?.substring(0, 10) ?? undefined

    const date = documentDate ? <span className="text-slate-600">({documentDate})</span> : null
    const metaData = documentType !== undefined || documentDate !== undefined
        ? <span className="text-slate-900">{documentType} {date}:</span> 
        : null

    return (
        <button 
            className="flex flex-row gap-2 items-center p-px text-pcx-500 hover:text-pcx-600 group text-sm"
            title={info.filename}
            onClick={() => {
            setIsDownloading(true)
            download().finally(() => setIsDownloading(false))
        }}>
            {metaData}
            <span className="group-hover:underline">{info?.filename ?? t('download')}</span> 
            {isDownloading ? <IconSpinner className="h-4 w-4 animate-spin text-pcx-500" /> : <div className="h-4 w-4"/>}
        </button>
    )
}

export function ActionView({status, renewal: renewal}: {status: DueDateStatus, renewal?: Renewal}) {
    const {t} = useTranslation()
    if (renewal === undefined || status === 'too-late' || renewal.id === undefined) {
        return null
    } else {
        return ( // TODO Adapt link URL
            <Link className="btn-secondary text-sm w-fit h-fit" to={`/_renewals/notices/instruct/${renewal.id}`}>
                {renewal?.isAutoPay ? t('edit') : t('instruct')}
            </Link>
        )
    }
}
